import { insertTextualBlockSoftBreak } from '@lumapps/wrex-textual-block/utils/insertTextualBlockSoftBreak';
import { TypographyEditor } from '@lumapps/wrex-typography/types';
import { createPlugin, CreatePluginEditor } from '@lumapps/wrex/slate/plugin';
import { normalizeLastBlock } from '@lumapps/wrex/slate/utils/normalizeLastBlock';
import type { WrexEditor } from '@lumapps/wrex/types';

import { CodeBlock } from '../components/blocks/CodeBlock';
import { CODE_BLOCK } from '../constants';
import { CodeBlockEditor } from '../types';
import { isCodeBlock } from '../utils/isCodeBlock';
import { isCodeBlockAllowed } from '../utils/isCodeBlockAllowed';
import { getKeyHandler } from './event/keyboard';

type Editor = WrexEditor<CodeBlockEditor & TypographyEditor>;

const createPluginEditor = (): CreatePluginEditor<CodeBlockEditor, Editor> => (editor) => {
    const { insertData, insertSoftBreak, normalizeNode } = editor;

    return {
        insertData(data: DataTransfer) {
            const text = data.getData('text/plain');
            // Always paste as plain text inside a code block.
            if (text && editor.getBlockType() === CODE_BLOCK) {
                editor.insertText(text);
            } else {
                insertData(data);
            }
        },

        insertSoftBreak() {
            insertTextualBlockSoftBreak(editor, isCodeBlock, insertSoftBreak);
        },

        isCodeBlockAllowed() {
            return isCodeBlockAllowed(editor);
        },

        normalizeNode(entry) {
            const [node] = entry;
            // Ensure a paragraph always follows a code block so the user can leave it.
            if (isCodeBlock(node)) {
                normalizeLastBlock(editor, entry, isCodeBlock);
            }
            normalizeNode(entry);
        },
    };
};

/**
 * Plugin rendering the code block element and its textual-block behavior.
 * Requires the typography plugin (for `changeBlockType` / `getBlockType`).
 */
export const withCodeBlock = () =>
    createPlugin({
        createPluginEditor: createPluginEditor(),
        getKeyHandler,
        elements: [CodeBlock],
    });
